import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { backendRequest, backendRequestAndNotification } from '../api'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { toast } from 'react-toastify'

const Tracking = ({ ws }) => {

    const [campaigns, setCampaigns] = useState([])
    const [filteredCampaigns, setFilteredCampaigns] = useState([])
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [lookup, setLookup] = useState('')
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        url: '',
        description: '',
        expiry_date: '',
        max_uses: ''
    })

    const history = useNavigate()
    const redirectToPage = (url) => history('/' + url)

    useEffect(() => {
        getCampaigns()
    }, [])

    useEffect(() => {
        // Filter campaigns based on search
        if (lookup.trim() === '') {
            setFilteredCampaigns(campaigns)
        } else {
            const filtered = campaigns.filter(campaign =>
                campaign.code.toLowerCase().includes(lookup.toLowerCase()) ||
                campaign.name.toLowerCase().includes(lookup.toLowerCase()) ||
                campaign.creator_name.toLowerCase().includes(lookup.toLowerCase())
            )
            setFilteredCampaigns(filtered)
        }
    }, [campaigns, lookup])

    const getCampaigns = async () => {
        try {
            const res = await backendRequest('/admin/tracking/get', 'GET')
            if (res.type === 'success') {
                setCampaigns(res.campaigns || [])
            } else {
                setCampaigns([])
            }
        } catch (e) {
            setCampaigns([])
            return
        }
    }

    const createCampaign = async () => {
        try {
            const res = await backendRequestAndNotification('/admin/tracking/create', 'POST', formData)
            if (res.type === 'success') {
                setFormData({ code: '', name: '', url: '', description: '', expiry_date: '', max_uses: '' })
                setShowCreateForm(false)
                getCampaigns() // Refresh the list
            } else {

            }
        } catch (e) {
            console.error('Error creating campaign:', e)
        }
    }

    const deleteCampaign = async (campaignId, campaignCode, e) => {
        e.stopPropagation() // Prevent row click

        if (window.confirm(`Are you sure you want to delete campaign "${campaignCode}"? This action cannot be undone.`)) {
            try {
                const res = await backendRequestAndNotification('/admin/tracking/delete', 'POST', { campaign_id: campaignId })
                if (res.type === 'success') {
                    getCampaigns() // Refresh the list
                }
            } catch (e) {
                console.error('Error deleting campaign:', e)
            }
        }
    }

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleDateString()
    }

    const copyTrackingLink = async (campaignCode, e) => {
        e.stopPropagation()

        const trackingUrl = `${window.location.origin}/trk/${campaignCode}`

        try {
            await navigator.clipboard.writeText(trackingUrl)
            toast.success(`Tracking link copied`, {
                position: "bottom-left",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: 'colored',
            })
        } catch (err) {
            console.error('Failed to copy: ', err)
        }
    }

    return (
        <>
            <div className='campaigns-container'>

                <div className='create-section'>
                    <button
                        className='create-btn'
                        onClick={() => setShowCreateForm(!showCreateForm)}
                    >
                        {showCreateForm ? 'Cancel' : 'Create New Campaign'}
                    </button>

                    {showCreateForm && (
                        <div className='create-form'>
                            <div className='form-row'>
                                <input
                                    placeholder='Campaign Code (e.g., SUMMER2025)'
                                    value={formData.code}
                                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                />
                                <input
                                    placeholder='Campaign Name'
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className='form-row'>
                                <input
                                    placeholder='Redirect URL (optional)'
                                    value={formData.url}
                                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                />
                                <input
                                    placeholder='Max Uses (optional)'
                                    type='number'
                                    value={formData.max_uses}
                                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                                />
                            </div>
                            <div className='form-row'>
                                <input
                                    placeholder='Description (optional)'
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                                <input
                                    placeholder='Expiry Date (optional)'
                                    type='date'
                                    value={formData.expiry_date}
                                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                                />
                            </div>
                            <button className='submit-btn' onClick={createCampaign}>
                                Create Campaign
                            </button>
                        </div>
                    )}
                </div>

                <div className='search'>
                    <input
                        placeholder='Search by code, name, or creator'
                        value={lookup}
                        onChange={(e) => setLookup(e.target.value)}
                    />
                    <button onClick={() => setLookup('')}>Clear</button>
                </div>

                <div className='table'>
                    <div className='table-header'>
                        <p>Code</p>
                        <p>Campaign Name</p>
                        <p>Creator</p>
                        <p>Clicks</p>
                        <p>Registers</p>
                        <p>Old Users</p>
                        <p>Earned</p>
                        <p>Created</p>
                        <p>Actions</p>
                    </div>

                    {Array.isArray(filteredCampaigns) && filteredCampaigns.map((campaign, index) => (
                        <div className='table-row' key={campaign._id} onClick={(e) => copyTrackingLink(campaign.code, e)} title={`Click to copy tracking link: /trk/${campaign.code}`}>
                            <p className='code'>{campaign.code}</p>
                            <p>{campaign.name}</p>
                            <p>{campaign.creator_name}</p>
                            <p className='blue'>{(campaign.clicks || 0).toLocaleString()}</p>
                            <p className='green'>{(campaign.total_registers || 0).toLocaleString()}</p>
                            <p className='green'>{(campaign.total_users || 0).toLocaleString()}</p>
                            <p className='green'>{(campaign.total_earned || 0).toLocaleString()}</p>
                            {/* <p className={campaign.status === 'active' ? 'green' : campaign.status === 'paused' ? 'blue' : ''}>
                                {campaign.status}
                            </p> */}
                            <p onClick={(e) => e.stopPropagation()}>{formatDate(campaign.created_at)}</p>
                            <p onClick={(e) => e.stopPropagation()}>
                                <span
                                    className='red'
                                    onClick={(e) => deleteCampaign(campaign._id, campaign.code, e)}
                                    title='Delete Campaign'
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </span>
                            </p>
                        </div>
                    ))}

                    {filteredCampaigns.length === 0 && lookup.trim() !== '' && (
                        <div className='no-results'>
                            <p>No campaigns found matching "{lookup}"</p>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .campaigns-container {
                    width: 100%;
                }

                .create-section {
                    margin-bottom: 20px;
                }

                .create-btn {
                    border: unset;
                    outline: unset;
                    padding: 12px 20px;
                    color: white;
                    background-color: #4FAC7C;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    margin-bottom: 15px;
                }

                .create-btn:hover {
                    background-color: #3e8a63;
                }

                .create-form {
                    background-color: #141A31;
                    padding: 20px;
                    border-radius: 12px;
                    margin-top: 10px;
                }

                .form-row {
                    display: flex;
                    gap: 15px;
                    margin-bottom: 15px;
                }

                .form-row input {
                    border: unset;
                    outline: unset;
                    flex: 1;
                    height: 45px;
                    color: white;
                    background-color: #21284C;
                    padding: 0px 15px;
                    border-radius: 6px;
                    font-size: 14px;
                }

                .form-row input::placeholder {
                    color: rgba(255, 255, 255, 0.5);
                }

                .submit-btn {
                    border: unset;
                    outline: unset;
                    width: 100%;
                    height: 45px;
                    color: white;
                    background-color: #4DAEFF;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                }

                .submit-btn:hover {
                    background-color: #3d8acc;
                }

                .search {
                    display: flex;
                    margin-bottom: 15px;
                    height: 50px;
                    gap: 15px;
                }

                .search input {
                    border: unset;
                    outline: unset;
                    width: 100%;
                    color: white;
                    background-color: #21284C;
                    padding: 0px 10px;
                    border-radius: 6px;
                }

                .search button {
                    border: unset;
                    outline: unset;
                    width: 150px;
                    color: white;
                    background-color: #4DAEFF;
                    border-radius: 6px;
                    cursor: pointer;
                }

                .table {
                    overflow-x: auto;
                }

                .table-header, .table-row {
                    background-color: #141A31;
                    display: flex;
                    border-radius: 12px;
                    padding: 0px 15px;
                    font-size: 14px;
                    align-items: center;
                    min-width: 900px;
                    height: 50px;
                }

                .table-row {
                    margin-top: 15px;
                    cursor: pointer;
                    transition: all .2s;
                }

                .table-row:hover {
                    background-color: #1e2648;
                }

                .table-row > * {
                    flex: 1;
                    text-align: center;
                }

                .table-header > * {
                    flex: 1;
                    text-align: center;
                    opacity: 0.5;
                }

                .green {
                    color: #4FAC7C;
                }

                .blue {
                    color: #4F8DDE;
                }

                .red {
                    color: #ac4f4f;
                }

                .code {
                    font-weight: 600;
                    color: #F1C475;
                }

                p {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            `}</style>
        </>
    )
}

export default Tracking